import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Download,
  ExternalLink,
  FileText,
  Film,
  Loader2,
  Megaphone,
  Paperclip,
  Plus,
  Send,
  Trash2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { FolderUploadButton } from '@/components/ui/FolderUploadButton';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Checkbox } from '@/components/ui/Checkbox';
import { Separator } from '@/components/ui/Separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { useAuth } from '@/app/AuthProvider';
import { useProfiles } from '@/features/users/hooks';
import { cn } from '@/lib/utils';
import type {
  SocialAdPlacement,
  SocialPlatform,
  SocialPostStatus,
  SocialPostType,
} from '@/types/database';
import {
  AD_PLACEMENTS,
  AD_PLACEMENT_LABELS,
  PLATFORM_ICONS,
  PLATFORM_LABELS,
  SOCIAL_PLATFORMS,
  SOCIAL_STATUSES,
  STATUS_LABELS,
  STATUS_PILL_CLASS,
} from '@/features/social/constants';
import {
  getSocialAssetSignedUrl,
  useAddSocialPostComment,
  useCreateMarketingCampaign,
  useCreateSocialPost,
  useDeleteSocialAsset,
  useDeleteSocialPost,
  useDeleteSocialPostComment,
  useMarketingCampaigns,
  useMarkTargetPublished,
  useSocialAssetSignedUrl,
  useSocialAssets,
  useSocialPostComments,
  useUpdateSocialPost,
  useUploadSocialAsset,
  type SocialPostTarget,
  type SocialPostWithDetails,
} from '@/features/social/hooks';

const MAX_ASSET_BYTES = 100 * 1024 * 1024; // 100 MB, same ceiling as tasks/calendar

const TIMEZONES = [
  'America/Chicago',
  'America/New_York',
  'America/Denver',
  'America/Los_Angeles',
  'UTC',
];

const NONE = '__none__';

interface PostDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Existing post to edit, or null to create a new one. */
  post: SocialPostWithDetails | null;
  /** Pre-filled date (`YYYY-MM-DD`) when creating from a day cell. */
  defaultDate?: string;
}

/**
 * Create / edit editor for a social post (SOCIAL.md §2.3): copy, type +
 * campaign (ad block), platform targets with per-platform copy
 * overrides, scheduling, assignee, workflow status, media + working
 * files, and the internal comment thread.
 */
export function PostDialog({ open, onOpenChange, post, defaultDate }: PostDialogProps) {
  const isEdit = !!post;

  const [title, setTitle] = useState('');
  const [masterCopy, setMasterCopy] = useState('');
  const [postType, setPostType] = useState<SocialPostType>('organic');
  const [status, setStatus] = useState<SocialPostStatus>('draft');
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [timezone, setTimezone] = useState('America/Chicago');
  const [platforms, setPlatforms] = useState<Set<SocialPlatform>>(new Set());
  const [copyOverrides, setCopyOverrides] = useState<
    Partial<Record<SocialPlatform, string>>
  >({});
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [adPlacement, setAdPlacement] = useState<SocialAdPlacement | null>(null);
  const [adBudget, setAdBudget] = useState('');
  const [adCta, setAdCta] = useState('');
  const [adDestUrl, setAdDestUrl] = useState('');

  // Re-seed the form every time the dialog opens (or switches target).
  useEffect(() => {
    if (!open) return;
    setTitle(post?.title ?? '');
    setMasterCopy(post?.master_copy ?? '');
    setPostType(post?.post_type ?? 'organic');
    setStatus(post?.status ?? 'draft');
    setAssigneeId(post?.assignee_id ?? null);
    setDueDate(post?.due_date ?? '');
    setScheduledDate(post?.scheduled_date ?? defaultDate ?? '');
    setScheduledTime(post?.scheduled_time?.slice(0, 5) ?? '');
    setTimezone(post?.timezone ?? 'America/Chicago');
    setPlatforms(new Set(post?.targets.map((t) => t.platform) ?? []));
    const overrides: Partial<Record<SocialPlatform, string>> = {};
    for (const t of post?.targets ?? []) {
      if (t.copy_override) overrides[t.platform] = t.copy_override;
    }
    setCopyOverrides(overrides);
    setCampaignId(post?.campaign_id ?? null);
    setAdPlacement(post?.ad_placement ?? null);
    setAdBudget(
      post?.ad_budget_cents != null ? (post.ad_budget_cents / 100).toString() : '',
    );
    setAdCta(post?.ad_call_to_action ?? '');
    setAdDestUrl(post?.ad_destination_url ?? '');
  }, [open, post, defaultDate]);

  const { data: profiles } = useProfiles();
  const members = useMemo(
    () =>
      (profiles ?? []).filter(
        (p) => p.is_active && p.role !== 'external_lender' && p.role !== 'employee',
      ),
    [profiles],
  );

  const createPost = useCreateSocialPost();
  const updatePost = useUpdateSocialPost();
  const deletePost = useDeleteSocialPost();
  const saving = createPost.isPending || updatePost.isPending;

  const togglePlatform = (platform: SocialPlatform) => {
    setPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(platform)) next.delete(platform);
      else next.add(platform);
      return next;
    });
  };

  const handleSave = async () => {
    const trimmed = title.trim();
    if (!trimmed) {
      toast.error('Give the post a title.');
      return;
    }
    if (postType === 'ad' && !campaignId) {
      toast.error('Ad posts must link to a campaign.');
      return;
    }
    const budgetCents =
      postType === 'ad' && adBudget.trim()
        ? Math.round(Number(adBudget) * 100)
        : null;
    if (budgetCents != null && (!Number.isFinite(budgetCents) || budgetCents < 0)) {
      toast.error('Ad budget must be a positive number.');
      return;
    }
    const input = {
      title: trimmed,
      master_copy: masterCopy,
      post_type: postType,
      status,
      assignee_id: assigneeId,
      due_date: dueDate || null,
      scheduled_date: scheduledDate || null,
      scheduled_time: scheduledTime || null,
      timezone,
      campaign_id: postType === 'ad' ? campaignId : campaignId ?? null,
      ad_placement: postType === 'ad' ? adPlacement : null,
      ad_budget_cents: budgetCents,
      ad_call_to_action: postType === 'ad' ? adCta.trim() || null : null,
      ad_destination_url: postType === 'ad' ? adDestUrl.trim() || null : null,
      platforms: [...platforms],
      copyOverrides,
    };
    try {
      if (post) {
        await updatePost.mutateAsync({
          id: post.id,
          existingTargets: post.targets,
          ...input,
        });
        toast.success('Post updated.');
      } else {
        await createPost.mutateAsync(input);
        toast.success('Post created.');
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save the post.');
    }
  };

  const handleDelete = async () => {
    if (!post) return;
    if (!window.confirm(`Delete “${post.title}”? This also removes its media and files.`)) {
      return;
    }
    try {
      await deletePost.mutateAsync(post.id);
      toast.success('Post deleted.');
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete the post.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit post' : 'New post'}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4">
          {/* ── Title + type ─────────────────────────────────────── */}
          <div className="grid gap-1.5">
            <Label htmlFor="post-title">Title</Label>
            <Input
              id="post-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Presale teaser — RV install timelapse"
              autoFocus
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="grid gap-1.5">
              <Label>Type</Label>
              <div className="flex h-10 items-center rounded-lg border border-input bg-card p-1">
                {(['organic', 'ad'] as SocialPostType[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setPostType(t)}
                    className={cn(
                      'flex h-full flex-1 items-center justify-center gap-1 rounded-md text-sm transition-colors',
                      postType === t
                        ? t === 'ad'
                          ? 'bg-violet-600 text-white'
                          : 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {t === 'ad' && <Megaphone className="h-3.5 w-3.5" />}
                    {t === 'organic' ? 'Organic' : 'Ad'}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label>Status</Label>
              <Select
                value={status}
                onValueChange={(v) => setStatus(v as SocialPostStatus)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOCIAL_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Assignee</Label>
              <Select
                value={assigneeId ?? NONE}
                onValueChange={(v) => setAssigneeId(v === NONE ? null : v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Unassigned</SelectItem>
                  {members.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.full_name || m.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ── Scheduling ───────────────────────────────────────── */}
          <div className="grid gap-4 sm:grid-cols-4">
            <div className="grid gap-1.5">
              <Label htmlFor="post-sched-date">Publish date</Label>
              <Input
                id="post-sched-date"
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="post-sched-time">Time</Label>
              <Input
                id="post-sched-time"
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Timezone</Label>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz} value={tz}>
                      {tz.replace('America/', '').replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="post-due-date">Due (internal)</Label>
              <Input
                id="post-due-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
          </div>

          {/* ── Copy ─────────────────────────────────────────────── */}
          <div className="grid gap-1.5">
            <Label htmlFor="post-copy">Master copy</Label>
            <Textarea
              id="post-copy"
              value={masterCopy}
              onChange={(e) => setMasterCopy(e.target.value)}
              placeholder="The platform-agnostic caption. Override per platform below where character limits / hashtag norms differ."
              rows={4}
            />
          </div>

          {/* ── Platform targets ─────────────────────────────────── */}
          <div className="grid gap-1.5">
            <Label>Platforms</Label>
            <div className="grid gap-1 rounded-lg border border-border p-2">
              {SOCIAL_PLATFORMS.map((platform) => {
                const Icon = PLATFORM_ICONS[platform];
                const selected = platforms.has(platform);
                const target = post?.targets.find((t) => t.platform === platform);
                return (
                  <div key={platform} className="rounded-md px-1 py-0.5">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selected}
                        onCheckedChange={() => togglePlatform(platform)}
                        ariaLabel={`Target ${PLATFORM_LABELS[platform]}`}
                      />
                      <button
                        type="button"
                        onClick={() => togglePlatform(platform)}
                        className="flex flex-1 items-center gap-2 py-1 text-left text-sm"
                      >
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        {PLATFORM_LABELS[platform]}
                      </button>
                      {target && (
                        <span
                          className={cn(
                            'rounded-full border px-2 py-0.5 text-[10px] font-medium',
                            STATUS_PILL_CLASS[target.status],
                          )}
                        >
                          {STATUS_LABELS[target.status]}
                        </span>
                      )}
                      {target?.external_url && (
                        <a
                          href={target.external_url}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="text-muted-foreground hover:text-foreground"
                          aria-label="Open live post"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                    {selected && (
                      <div className="ml-6 mt-1 grid gap-1 pb-1">
                        <Textarea
                          value={copyOverrides[platform] ?? ''}
                          onChange={(e) =>
                            setCopyOverrides((prev) => ({
                              ...prev,
                              [platform]: e.target.value,
                            }))
                          }
                          placeholder={`Optional ${PLATFORM_LABELS[platform]} copy override — leave empty to use the master copy.`}
                          rows={2}
                          className="text-xs"
                        />
                        {target && target.status !== 'published' && isEdit && (
                          <MarkPublishedInline target={target} isAd={postType === 'ad'} />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Ad block ─────────────────────────────────────────── */}
          {postType === 'ad' && (
            <div className="grid gap-4 rounded-lg border border-violet-300 bg-violet-50/50 p-3">
              <p className="flex items-center gap-1.5 text-xs font-medium text-violet-900">
                <Megaphone className="h-3.5 w-3.5" />
                Ad details — trafficked manually in Ads Manager for now; paste the ad
                ID back on each platform row once live.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-1.5">
                  <Label>Campaign (required)</Label>
                  <CampaignPicker value={campaignId} onChange={setCampaignId} />
                </div>
                <div className="grid gap-1.5">
                  <Label>Placement</Label>
                  <Select
                    value={adPlacement ?? NONE}
                    onValueChange={(v) =>
                      setAdPlacement(v === NONE ? null : (v as SocialAdPlacement))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Not set</SelectItem>
                      {AD_PLACEMENTS.map((p) => (
                        <SelectItem key={p} value={p}>
                          {AD_PLACEMENT_LABELS[p]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="ad-budget">Budget ($)</Label>
                  <Input
                    id="ad-budget"
                    type="number"
                    min="0"
                    step="0.01"
                    value={adBudget}
                    onChange={(e) => setAdBudget(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="ad-cta">Call to action</Label>
                  <Input
                    id="ad-cta"
                    value={adCta}
                    onChange={(e) => setAdCta(e.target.value)}
                    placeholder="e.g. Shop Now"
                  />
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="ad-dest">Destination URL</Label>
                <Input
                  id="ad-dest"
                  value={adDestUrl}
                  onChange={(e) => setAdDestUrl(e.target.value)}
                  placeholder="https://virrolife.com/…"
                />
              </div>
            </div>
          )}

          {/* Organic posts can still tag a campaign for the rollup view. */}
          {postType === 'organic' && (
            <div className="grid gap-1.5">
              <Label>Campaign (optional)</Label>
              <CampaignPicker value={campaignId} onChange={setCampaignId} />
            </div>
          )}

          {/* ── Media / files / comments (existing posts only) ───── */}
          {isEdit && post ? (
            <>
              <Separator />
              <AssetsSection postId={post.id} />
              <Separator />
              <CommentsSection postId={post.id} />
            </>
          ) : null}
        </div>

        <DialogFooter className="sm:justify-between">
          {isEdit ? (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deletePost.isPending || saving}
            >
              {deletePost.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Delete
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? 'Save changes' : 'Create post'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Campaign picker — select an existing marketing campaign or create one
// inline (paid_social channel).
// ---------------------------------------------------------------------------

const NEW_CAMPAIGN = '__new__';

function CampaignPicker({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (campaignId: string | null) => void;
}) {
  const { data: campaigns } = useMarketingCampaigns();
  const createCampaign = useCreateMarketingCampaign();

  const handleChange = async (v: string) => {
    if (v === NONE) {
      onChange(null);
      return;
    }
    if (v === NEW_CAMPAIGN) {
      const name = window.prompt('New campaign name:');
      if (!name?.trim()) return;
      try {
        const campaign = await createCampaign.mutateAsync({ name });
        onChange(campaign.id);
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Could not create the campaign.',
        );
      }
      return;
    }
    onChange(v);
  };

  return (
    <Select value={value ?? NONE} onValueChange={(v) => void handleChange(v)}>
      <SelectTrigger>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>No campaign</SelectItem>
        {(campaigns ?? []).map((c) => (
          <SelectItem key={c.id} value={c.id}>
            {c.name}
          </SelectItem>
        ))}
        <SelectItem value={NEW_CAMPAIGN}>
          <span className="flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5" />
            New campaign…
          </span>
        </SelectItem>
      </SelectContent>
    </Select>
  );
}

// ---------------------------------------------------------------------------
// Manual publish flow (Phase 1): paste the live URL / ad id back in.
// ---------------------------------------------------------------------------

function MarkPublishedInline({
  target,
  isAd,
}: {
  target: SocialPostTarget;
  isAd: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [url, setUrl] = useState(target.external_url ?? '');
  const [adId, setAdId] = useState(target.external_ad_id ?? '');
  const markPublished = useMarkTargetPublished();

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="w-fit text-[11px] font-medium text-primary hover:underline"
      >
        Posted it by hand? Mark published…
      </button>
    );
  }

  const handleMark = async () => {
    try {
      await markPublished.mutateAsync({
        target,
        external_url: url.trim() || null,
        external_post_id: null,
        external_ad_id: adId.trim() || null,
      });
      toast.success(`${PLATFORM_LABELS[target.platform]} marked published.`);
      setExpanded(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Could not mark the target published.',
      );
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="Live post URL"
        className="h-8 max-w-64 text-xs"
      />
      {isAd && (
        <Input
          value={adId}
          onChange={(e) => setAdId(e.target.value)}
          placeholder="Ad ID (from Ads Manager)"
          className="h-8 max-w-44 text-xs"
        />
      )}
      <Button
        size="sm"
        variant="secondary"
        onClick={() => void handleMark()}
        disabled={markPublished.isPending}
      >
        {markPublished.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        Mark published
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setExpanded(false)}>
        Cancel
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Media + working files
// ---------------------------------------------------------------------------

function AssetsSection({ postId }: { postId: string }) {
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { data: assets, isLoading } = useSocialAssets(postId);
  const upload = useUploadSocialAsset();
  const remove = useDeleteSocialAsset();

  const media = (assets ?? []).filter((a) => a.is_publishable);
  const workingFiles = (assets ?? []).filter((a) => !a.is_publishable);

  const handleFiles = async (files: FileList | File[] | null, isPublishable: boolean) => {
    if (!files || files.length === 0) return;
    let sortOrder = media.length;
    for (const file of Array.from(files)) {
      if (file.size > MAX_ASSET_BYTES) {
        toast.error(`“${file.name}” is larger than 100 MB.`);
        continue;
      }
      try {
        await upload.mutateAsync({
          postId,
          file,
          isPublishable,
          sortOrder: isPublishable ? sortOrder++ : 0,
        });
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : `Could not upload “${file.name}”.`,
        );
      }
    }
    if (mediaInputRef.current) mediaInputRef.current.value = '';
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownload = async (path: string) => {
    try {
      const url = await getSocialAssetSignedUrl(path);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not open the file.');
    }
  };

  const handleDelete = (asset: { id: string; storage_path: string }) =>
    void remove
      .mutateAsync({ id: asset.id, postId, storagePath: asset.storage_path })
      .catch((err: unknown) =>
        toast.error(err instanceof Error ? err.message : 'Could not delete the file.'),
      );

  return (
    <div className="grid gap-3">
      {/* Publishable media */}
      <div className="grid gap-1.5">
        <div className="flex items-center justify-between">
          <Label>Media (publishes with the post)</Label>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => mediaInputRef.current?.click()}
              disabled={upload.isPending}
            >
              {upload.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Plus className="h-3.5 w-3.5" />
              )}
              Add media
            </Button>
            <FolderUploadButton
              size="sm"
              accept="image/*,video/*"
              disabled={upload.isPending}
              onFiles={(files) => void handleFiles(files, true)}
            />
          </div>
          <input
            ref={mediaInputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            className="hidden"
            onChange={(e) => void handleFiles(e.target.files, true)}
          />
        </div>
        {isLoading ? (
          <p className="text-xs text-muted-foreground">Loading media…</p>
        ) : media.length === 0 ? (
          <p className="text-xs text-muted-foreground">No media yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {media.map((a) => (
              <MediaThumb
                key={a.id}
                asset={a}
                onOpen={() => void handleDownload(a.storage_path)}
                onDelete={() => handleDelete(a)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Working files */}
      <div className="grid gap-1.5">
        <div className="flex items-center justify-between">
          <Label>Working files (internal only)</Label>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={upload.isPending}
            >
              <Paperclip className="h-3.5 w-3.5" />
              Attach files
            </Button>
            <FolderUploadButton
              size="sm"
              disabled={upload.isPending}
              onFiles={(files) => void handleFiles(files, false)}
            />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => void handleFiles(e.target.files, false)}
          />
        </div>
        {workingFiles.length === 0 ? (
          <p className="text-xs text-muted-foreground">No working files yet.</p>
        ) : (
          <ul className="grid gap-1">
            {workingFiles.map((a) => (
              <li
                key={a.id}
                className="flex items-center gap-2 rounded-lg border border-border bg-card px-2.5 py-1.5 text-sm"
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate" title={a.file_name}>
                  {a.file_name}
                </span>
                {a.size_bytes != null && (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {formatBytes(a.size_bytes)}
                  </span>
                )}
                <button
                  type="button"
                  aria-label={`Download ${a.file_name}`}
                  onClick={() => void handleDownload(a.storage_path)}
                  className="shrink-0 rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
                >
                  <Download className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  aria-label={`Delete ${a.file_name}`}
                  onClick={() => handleDelete(a)}
                  className="shrink-0 rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function MediaThumb({
  asset,
  onOpen,
  onDelete,
}: {
  asset: { id: string; storage_path: string; file_name: string; kind: string };
  onOpen: () => void;
  onDelete: () => void;
}) {
  const { data: url } = useSocialAssetSignedUrl(
    asset.kind === 'image' ? asset.storage_path : undefined,
  );
  return (
    <div className="group relative h-20 w-20 overflow-hidden rounded-lg border border-border bg-secondary/40">
      <button
        type="button"
        onClick={onOpen}
        className="h-full w-full"
        title={asset.file_name}
      >
        {asset.kind === 'image' && url ? (
          <img src={url} alt={asset.file_name} className="h-full w-full object-cover" />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-muted-foreground">
            {asset.kind === 'video' ? (
              <Film className="h-6 w-6" />
            ) : (
              <FileText className="h-6 w-6" />
            )}
          </span>
        )}
      </button>
      <button
        type="button"
        aria-label={`Delete ${asset.file_name}`}
        onClick={onDelete}
        className="absolute right-1 top-1 rounded bg-background/80 p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

function CommentsSection({ postId }: { postId: string }) {
  const { profile } = useAuth();
  const [body, setBody] = useState('');
  const { data: comments, isLoading } = useSocialPostComments(postId);
  const addComment = useAddSocialPostComment();
  const deleteComment = useDeleteSocialPostComment();

  const handleAdd = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    try {
      await addComment.mutateAsync({ postId, body: trimmed });
      setBody('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not post the comment.');
    }
  };

  return (
    <div className="grid gap-2">
      <Label>Comments</Label>
      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading comments…</p>
      ) : (comments ?? []).length === 0 ? (
        <p className="text-xs text-muted-foreground">No comments yet.</p>
      ) : (
        <ul className="grid gap-2">
          {(comments ?? []).map((c) => (
            <li key={c.id} className="flex items-start gap-2">
              <Avatar className="h-7 w-7">
                {c.author?.avatar_url && <AvatarImage src={c.author.avatar_url} />}
                <AvatarFallback>
                  {initials(c.author?.full_name || c.author?.email || '?')}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1 rounded-lg bg-secondary/40 px-2.5 py-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium">
                    {c.author?.full_name || c.author?.email || 'Unknown'}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(c.created_at).toLocaleString()}
                  </span>
                  {(c.author_id === profile?.id || profile?.role === 'super_admin') && (
                    <button
                      type="button"
                      aria-label="Delete comment"
                      onClick={() =>
                        void deleteComment
                          .mutateAsync({ id: c.id, postId })
                          .catch((err: unknown) =>
                            toast.error(
                              err instanceof Error
                                ? err.message
                                : 'Could not delete the comment.',
                            ),
                          )
                      }
                      className="ml-auto rounded p-0.5 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <p className="whitespace-pre-wrap text-sm">{c.body}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-end gap-1.5">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Leave a note for the team…"
          rows={2}
          className="text-sm"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void handleAdd();
            }
          }}
        />
        <Button
          size="sm"
          onClick={() => void handleAdd()}
          disabled={!body.trim() || addComment.isPending}
          aria-label="Post comment"
        >
          {addComment.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
